import { features, hasFeatureAccess } from 'src/config/features-config';
import types from '../mutation-types';
import featureControlService from 'src/services/featureControl';

// Identifies whose subscriptions are in the store, so a list fetched for a
// previous organization or user is never trusted for the current one.
const subscriptionScope = () =>
  `${localStorage.getItem('organization')}:${localStorage.getItem('id')}`;

// Most recent subscriptions request that has not settled yet.
let pendingSubscriptionsRequest = null;
let subscriptionsRequestSeq = 0;
let committedSubscriptionsSeq = 0;

const toList = (data) => (Array.isArray(data) ? data : data?.results || []);

const state = {
  adminFeatures: [],
  adminFeatureSubscriptions: [],
  isLoadingAdminFeatureControl: false,
  featureSubscriptions: [],
  // Scope of the last successful subscriptions fetch; null while a fetch is
  // in flight, after a failed fetch, or before the first one.
  featureSubscriptionsLoadedFor: null,
};

const getters = {
  adminFeatures: (state) => state.adminFeatures,
  adminFeatureSubscriptions: (state) => state.adminFeatureSubscriptions,
  isLoadingAdminFeatureControl: (state) => state.isLoadingAdminFeatureControl,
  featureSubscriptions: (state) => state.featureSubscriptions,
  // False until subscriptions have loaded (unless the role bypasses the check).
  hasValueStoryAccess: (state, getters, rootState, rootGetters) =>
    hasFeatureAccess(
      features.value_story,
      rootGetters.role,
      state.featureSubscriptions
    ),
};

const actions = {
  // ---- Admin Feature Control page -------------------------------------
  // Kept apart from featureSubscriptions above, which holds the current
  // organization's own subscriptions and drives the customer portal.
  async fetchAdminFeatureControl({ commit }) {
    commit(types.SET_IS_LOADING_ADMIN_FEATURE_CONTROL, true);
    const [[featuresError, features], [subscriptionsError, subscriptions]] =
      await Promise.all([
        featureControlService.fetchAdminFeatures(),
        featureControlService.fetchAdminFeatureSubscriptions(),
      ]);
    commit(types.SET_IS_LOADING_ADMIN_FEATURE_CONTROL, false);
    const error = featuresError || subscriptionsError;
    if (error) return [error];
    commit(types.SET_ADMIN_FEATURE_CONTROL, {
      features: toList(features),
      subscriptions: toList(subscriptions),
    });
    return [null];
  },

  async adminCreateFeature({ dispatch }, name) {
    const [error, feature] = await featureControlService.createFeature(
      { name },
      false
    );
    if (error) return [error];
    await dispatch('fetchAdminFeatureControl');
    return [null, feature];
  },

  async adminDisableFeature({ dispatch }, featureId) {
    const [error] = await featureControlService.suppressFeature(
      featureId,
      {},
      false
    );
    if (error) return [error];
    await dispatch('fetchAdminFeatureControl');
    return [null];
  },

  async adminRestoreFeature({ dispatch }, featureId) {
    const [error] = await featureControlService.restoreFeature(featureId);
    if (error) return [error];
    await dispatch('fetchAdminFeatureControl');
    return [null];
  },

  async adminEnableFeatureForOrganization(
    { dispatch },
    { featureId, organization }
  ) {
    const [error] = await featureControlService.createFeatureSubscription(
      { Feature: featureId, Organization: organization },
      false
    );
    if (error) return [error];
    await dispatch('fetchAdminFeatureControl');
    return [null];
  },

  async adminRemoveFeatureSubscription({ dispatch }, subscriptionId) {
    const [error] = await featureControlService.deleteSubscriptionFeature(
      subscriptionId,
      {},
      false
    );
    if (error) return [error];
    await dispatch('fetchAdminFeatureControl');
    return [null];
  },

  async getAllFeatureSubscriptions({ commit }) {
    const request = {
      scope: subscriptionScope(),
      seq: ++subscriptionsRequestSeq,
    };
    pendingSubscriptionsRequest = request;
    commit(types.SET_FEATURE_SUBSCRIPTIONS_LOADED_FOR, null);

    request.promise = (async () => {
      let featureSubscriptions = [];
      let loaded = false;
      try {
        const [err, data] =
          await featureControlService.fetchAllFeatureSubscriptions(false);
        if (!err) {
          featureSubscriptions = data || [];
          loaded = true;
        }
      } catch (e) {
        loaded = false;
      }
      if (pendingSubscriptionsRequest === request) {
        pendingSubscriptionsRequest = null;
      }
      // Keep every result as it arrives (browsers may hold a duplicate
      // in-flight request until the first one finishes), but never let an
      // older response overwrite a newer one already in the store.
      if (request.seq < committedSubscriptionsSeq) return;
      committedSubscriptionsSeq = request.seq;
      commit(types.SET_FEATURE_SUBSCRIPTIONS, featureSubscriptions);
      commit(
        types.SET_FEATURE_SUBSCRIPTIONS_LOADED_FOR,
        loaded ? request.scope : null
      );
    })();
    return request.promise;
  },

  // Resolves true once the store holds a real fetch result for the current
  // organization and user, fetching (or awaiting an in-flight fetch) if
  // needed. Resolves false when the fetch failed.
  async ensureFeatureSubscriptions({ state, dispatch }) {
    const scope = subscriptionScope();
    if (state.featureSubscriptionsLoadedFor !== scope) {
      if (pendingSubscriptionsRequest?.scope === scope) {
        await pendingSubscriptionsRequest.promise;
      } else {
        await dispatch('getAllFeatureSubscriptions');
      }
    }
    return state.featureSubscriptionsLoadedFor === subscriptionScope();
  },

  setFeatureSubscription({ commit }, payload) {
    commit(types.SET_FEATURE_SUBSCRIPTIONS, payload);
  },
};

const mutations = {
  [types.SET_ADMIN_FEATURE_CONTROL](state, { features, subscriptions }) {
    state.adminFeatures = features;
    state.adminFeatureSubscriptions = subscriptions;
  },
  [types.SET_IS_LOADING_ADMIN_FEATURE_CONTROL](state, payload) {
    state.isLoadingAdminFeatureControl = payload;
  },
  [types.SET_FEATURE_SUBSCRIPTIONS](state, payload) {
    state.featureSubscriptions = [...payload];
  },
  [types.SET_FEATURE_SUBSCRIPTIONS_LOADED_FOR](state, payload) {
    state.featureSubscriptionsLoadedFor = payload;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
